import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { RepositoryType } from '@repo/domain';
import { createGraphQLClient, type GraphQlClient, Provider as UrqlProvider } from '@repo/graphql';
import { initI18n } from '@repo/locale';
import { GeoPositionProvider } from '@repo/geo-position';
import { MockRepository } from '@repo/repo-mock';
import { getAccessToken, handleAccessTokenStatus, ParkAppRepository } from '@repo/repo-parkapp';
import { ParkAppTypesenseRepository } from '@repo/repo-parkapp-typesense';
import { GoogleGeocodeRepository } from '@repo/repo-google-geocode';
import { ToasterProvider } from '@repo/toast';
import { DomainQueryProvider, initRepositoryRegistry, setUnauthenticatedHandler } from '@repo/domain-service';
import { initFeatureFlagClient } from '@repo/feature-flag';
import { ErrorBoundary, ErrorPage, initErrorHandling } from '@repo/error';
import { isApplicationMode } from '@repo/env-variables';
import { usePrivacyConsentStore, PrivacyConsentsModalWrapper } from '@repo/privacy-consent';
import { TestId } from '@repo/test-id';

import { useAuthStore, useHeldRolesStore, logout, finalizeLogin, selectStatus } from '@features/auth';
import { captureSpanPaymentLinkSignatureFromUrl } from '@features/reservation';

import { AppRouterProvider } from '@providers/AppRouterProvider';
import { ThemeProvider } from '@providers/ThemeProvider';

import { getConfig, configError } from '@shared/env';
import MaintenancePage from '@shared/pages/MaintenancePage';
import { useLanguageStore } from '@shared/stores/useLanguageStore';
import { captureMaintenanceBypassFromUrl, hasMaintenanceBypass } from '@shared/utils/maintenanceBypassStorage';
import { applyStoredFeatureFlagOverrides } from '@shared/utils/applyStoredFeatureFlagOverrides';
import { captureReferralCodeFromUrl } from '@shared/utils/referralCodeStorage';
import { initWebStatistics } from '@shared/utils/initWebStatistics';

import '@fontsource-variable/outfit';

import '@styles/index.css';

captureMaintenanceBypassFromUrl();
// Before anything reads the URL: the signature of a payment link must not reach Sentry, Analytics or a referrer.
captureSpanPaymentLinkSignatureFromUrl();

const maintenancePlannedReturnDate: string | undefined = configError
  ? undefined
  : getConfig('maintenancePlannedReturnDate');

if (configError) {
  await initI18n(useLanguageStore.getState().language);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorPage source="config" />
    </StrictMode>
  );
} else if (maintenancePlannedReturnDate !== undefined && !hasMaintenanceBypass()) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MaintenancePage plannedReturnDate={maintenancePlannedReturnDate} />
    </StrictMode>
  );
} else {
  // Two tokens travel together: the long-lived session token from the auth store
  // (`Authorization: Bearer`) and the short-lived access token owned by
  // repo-parkapp (`X-Access-Token`). The backend accepts either.
  // Requests carrying a file go to core directly: the app router does not accept file uploads.
  const graphQLClient: GraphQlClient = createGraphQLClient({
    url: getConfig('graphqlUrl'),
    uploadUrl: `${getConfig('legacyApiUrl')}/graphql`,
    getToken: (): string | undefined => useAuthStore.getState().accessToken,
    getLanguage: (): string | undefined => useLanguageStore.getState().language,
    getAccessToken,
    onAccessTokenStatus: handleAccessTokenStatus,
  });

  const repos: RepositoryType[] = [];

  if (getConfig('useMockRepository')) {
    repos.push(
      new MockRepository({
        onAuthenticated: (token) => useAuthStore.getState().setAccessToken(token),
        onLoginComplete: (user) => finalizeLogin(user),
        isGuestSession: () => selectStatus(useAuthStore.getState()) === 'guest',
      })
    );
  } else {
    repos.push(
      new ParkAppTypesenseRepository({
        host: getConfig('parkingSearchHost'),
        port: getConfig('parkingSearchPort'),
        protocol: getConfig('parkingSearchProtocol'),
        key: getConfig('parkingSearchKey'),
        collection: getConfig('parkingSearchCollection'),
      })
    );
    repos.push(
      new ParkAppRepository({
        client: graphQLClient,
        apiUrl: getConfig('restApiUrl'),
        getToken: () => useAuthStore.getState().accessToken,
        getLanguage: () => useLanguageStore.getState().language,
        onAuthenticated: (token) => useAuthStore.getState().setAccessToken(token),
        onLoginComplete: (user) => finalizeLogin(user),
        isGuestSession: () => selectStatus(useAuthStore.getState()) === 'guest',
        onHeldRolesChange: (heldRoles) => useHeldRolesStore.getState().setHeldRoles(heldRoles),
      })
    );
    repos.push(new GoogleGeocodeRepository({ googleApiKey: getConfig('mapsKey') }));
  }

  initFeatureFlagClient(isApplicationMode('dev'));

  if (isApplicationMode('dev')) {
    applyStoredFeatureFlagOverrides();
  }

  captureReferralCodeFromUrl();

  const isBugTrackingConsented = (): boolean => usePrivacyConsentStore.getState().consents.bugTracking === true;

  const initErrorTracking = (): void => {
    void initErrorHandling({
      isDev: isApplicationMode('dev'),
      dsn: getConfig('sentryDsn'),
      consentGiven: isBugTrackingConsented(),
      release: __SENTRY_RELEASE__,
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initErrorTracking);
  } else {
    setTimeout(initErrorTracking, 0);
  }

  type PrivacyConsentState = ReturnType<typeof usePrivacyConsentStore.getState>;
  usePrivacyConsentStore.subscribe((state: PrivacyConsentState, prevState: PrivacyConsentState) => {
    const next = state.consents.bugTracking === true;
    const prev = prevState.consents.bugTracking === true;
    if (next === prev) return;
    void initErrorHandling({
      isDev: isApplicationMode('dev'),
      dsn: getConfig('sentryDsn'),
      consentGiven: next,
      release: __SENTRY_RELEASE__,
    });
  });

  initWebStatistics();

  initRepositoryRegistry(repos);

  setUnauthenticatedHandler(() => logout());

  await initI18n(useLanguageStore.getState().language);
  const consentsTestId = TestId.root('consents');

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <DomainQueryProvider>
          <UrqlProvider value={graphQLClient}>
            <ThemeProvider>
              <GeoPositionProvider>
                <AppRouterProvider />
                <ToasterProvider />
                <PrivacyConsentsModalWrapper
                  testId={consentsTestId}
                  serviceIds={[
                    'authorization',
                    'parkingReservation',
                    'googleMaps',
                    'chunkRefresh',
                    'language',
                    'parkingSearch',
                    'splashScreenHider',
                    'theme',
                    'parkingCreate',
                    'locationPermission',
                    'googleReverseGeocode',
                    'privacyConsent',
                    'bugTracking',
                    'googleAnalytics',
                  ]}
                />
              </GeoPositionProvider>
            </ThemeProvider>
          </UrqlProvider>
        </DomainQueryProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
